import { useAppDispatch } from '@/hooks';
import { toggleEditStake } from '@/store';
import { useLogoutMutation } from '@/store/api/authApi';
import { setRulesModalOpen, toggleAgeModalOpen } from '@/store/slices/uiSlice';
import { Icons } from '@/utils/Icons';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useNavigate } from 'react-router-dom';

type UserDropdownProps = {
  isDemoUser: boolean;
  username: string;
  showBalance: boolean;
  showExposure: boolean;
  handleBalanceChange: (value: boolean) => void;
  handleExposureChange: (value: boolean) => void;
};
const UserDropdown: React.FC<UserDropdownProps> = ({
  isDemoUser,
  username,
  handleBalanceChange,
  handleExposureChange,
  showBalance,
  showExposure,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [logout] = useLogoutMutation();

  const selectItems: { label: string; path: string; protected?: boolean }[] = [
    { label: 'Account Statement', path: '/user/account-statement' },
    { label: 'Current Bet', path: '/user/bet-history' },
    { label: 'Casino Results', path: '/user/casino-results' },
  ];

  const itemClass =
    "flex items-center py-[6px] px-[12px] text-[16px] font-normal font-['Roboto_Condensed'] text-[#000000] cursor-pointer outline-none transition-colors hover:bg-gray-100 focus:bg-gray-100 data-[highlighted]:bg-gray-100";

const handleLogout = async () => {
  try {
    const res = await logout().unwrap();
  } catch (e) {
    console.log(e);
  }
};

return (
  <DropdownMenu.Root>
    <DropdownMenu.Trigger asChild>
      <button className="group flex items-center gap-1 text-white text-xs md:text-base font-semibold rounded-md px-1.5 py-1 transition hover:bg-white/10 focus:outline-none data-[state=open]:bg-white/10">
        <span className="max-w-[90px] truncate">{isDemoUser ? 'Demo' : username}</span>
        <Icons.Down style={{ width: '12px', height: '13px', fontSize: '13px' }} />
      </button>
    </DropdownMenu.Trigger>

    <DropdownMenu.Portal>
      <DropdownMenu.Content
        align="end"
        sideOffset={6}
        className="min-w-[200px] bg-white text-gray-800 rounded-md shadow-2xl border border-gray-200 py-2 px-0 overflow-hidden z-50"
      >
        {/* Dynamic navigation items */}
        {selectItems.map((item) => {
          if (item?.protected && isDemoUser) return;
          return (
            <DropdownMenu.Item
              key={item.path}
              className={itemClass}
              onSelect={() => navigate(item.path)}
            >
              {item.label}
            </DropdownMenu.Item>
          );
        })}

        {/* Redux actions */}
        <DropdownMenu.Item className={itemClass} onSelect={() => dispatch(toggleEditStake())}>
          Set Button Values
        </DropdownMenu.Item>

        <DropdownMenu.Item
          className={`${itemClass} xl:hidden`}
          onSelect={() => dispatch(setRulesModalOpen(true))}
        >
          Rules
        </DropdownMenu.Item>

        <DropdownMenu.Separator className="h-px bg-gray-100 my-1 xl:hidden" />

        <DropdownMenu.Item
          className={`${itemClass} justify-between xl:hidden`}
          onSelect={(e) => {
            e.preventDefault();
            handleBalanceChange(!showBalance);
          }}
        >
          <span>Balance</span>
          <input
            type="checkbox"
            checked={showBalance}
            readOnly
            className="w-4 h-4 rounded accent-[#2c3e50] pointer-events-none"
          />
        </DropdownMenu.Item>

        <DropdownMenu.Item
          className={`${itemClass} justify-between xl:hidden`}
          onSelect={(e) => {
            e.preventDefault();
            handleExposureChange(!showExposure);
          }}
        >
          <span>Exposure</span>
          <input
            type="checkbox"
            checked={showExposure}
            readOnly
            className="w-4 h-4 rounded accent-[#2c3e50] pointer-events-none"
          />
        </DropdownMenu.Item>

        <DropdownMenu.Separator className="h-px bg-gray-200 my-1" />
        <DropdownMenu.Item className={itemClass} onSelect={handleLogout}>
          SignOut
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu.Portal>
  </DropdownMenu.Root>
);
};

export default UserDropdown;
